<script setup lang="ts">
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { FileText, MessageSquareText, Paperclip, Repeat, Share2 } from 'lucide-vue-next';
import type { Comment, Task, UploadedFile } from '~/types/models';

const props = withDefaults(defineProps<{
  task: Task;
  files?: UploadedFile[];
  comments?: Comment[];
  showComments?: boolean;
}>(), {
  files: () => [],
  comments: () => [],
  showComments: true,
});

const emit = defineEmits<{
  (event: 'open'): void;
}>();

const descriptionPreview = computed(() => {
  const description = String(props.task.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!description) return '';
  return description.length > 160 ? `${description.slice(0, 157)}...` : description;
});

const latestComment = computed(() => {
  const comment = props.comments[props.comments.length - 1];
  if (!comment) return null;
  return {
    content: comment.content,
    authorName: comment.authorName || 'Unknown',
    createdAt: formatUnknownDate(comment.createdAt),
  };
});

const recurringSummary = computed(() => {
  const recurring = props.task.recurring;
  if (!recurring?.enabled) return '';
  if (recurring.frequency === 'weekly' && recurring.daysOfWeek?.length) {
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `Repeats weekly on ${recurring.daysOfWeek.map(day => weekdays[day]).filter(Boolean).join(', ')}`;
  }
  if (recurring.frequency === 'monthly' && recurring.dayOfMonth) return `Repeats monthly on day ${recurring.dayOfMonth}`;
  return `Repeats ${recurring.frequency || 'daily'}`;
});

function formatUnknownDate(value?: unknown) {
  if (!value) return 'Unknown';
  if (typeof value === 'object' && value !== null && 'toDate' in value) value = (value as { toDate: () => Date }).toDate();
  else if (typeof value === 'object' && value !== null && 'seconds' in value) value = new Date(Number((value as { seconds: number }).seconds) * 1000);
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}
</script>

<template>
  <div class="flex items-center gap-1">
    <HoverCard v-if="descriptionPreview">
      <HoverCardTrigger as-child>
        <button type="button" class="task-metadata-button text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Open task description" @click.stop="emit('open')"><FileText class="size-3.5" /></button>
      </HoverCardTrigger>
      <HoverCardContent class="w-72">
        <p class="text-sm font-medium">Description</p>
        <p class="mt-2 text-xs leading-5 text-foreground/80">{{ descriptionPreview }}</p>
      </HoverCardContent>
    </HoverCard>

    <HoverCard v-if="task.clientVisible">
      <HoverCardTrigger as-child>
        <button type="button" class="task-metadata-button text-sky-600/60 hover:bg-sky-50 hover:text-sky-700" aria-label="Open client sharing details" @click.stop="emit('open')"><Share2 class="size-3.5" /></button>
      </HoverCardTrigger>
      <HoverCardContent class="w-56"><p class="text-sm font-medium">Shared in client portal</p><p class="mt-1 text-xs text-muted-foreground">This task is visible to the client in the portal.</p></HoverCardContent>
    </HoverCard>

    <HoverCard v-if="files.length">
      <HoverCardTrigger as-child>
        <button type="button" class="task-metadata-button text-violet-600/60 hover:bg-violet-50 hover:text-violet-700" :aria-label="`Open ${files.length} task attachment${files.length === 1 ? '' : 's'}`" @click.stop="emit('open')"><Paperclip class="size-3.5" /></button>
      </HoverCardTrigger>
      <HoverCardContent class="w-64">
        <p class="text-sm font-medium">{{ files.length }} file{{ files.length === 1 ? '' : 's' }}</p>
        <ul class="mt-2 space-y-1 text-xs text-muted-foreground"><li v-for="file in files.slice(0, 3)" :key="file.id" class="truncate">{{ file.fileName }}</li></ul>
      </HoverCardContent>
    </HoverCard>

    <HoverCard v-if="showComments && comments.length">
      <HoverCardTrigger as-child>
        <button type="button" class="task-metadata-button text-amber-600/60 hover:bg-amber-50 hover:text-amber-700" :aria-label="`Open ${comments.length} task comment${comments.length === 1 ? '' : 's'}`" @click.stop="emit('open')"><MessageSquareText class="size-3.5" /></button>
      </HoverCardTrigger>
      <HoverCardContent class="w-64">
        <p class="text-sm font-medium">{{ comments.length }} comment{{ comments.length === 1 ? '' : 's' }}</p>
        <template v-if="latestComment"><p class="mt-2 text-xs text-foreground/80">{{ latestComment.content }}</p><p class="mt-1 text-xs text-muted-foreground">{{ latestComment.authorName }} on {{ latestComment.createdAt }}</p></template>
      </HoverCardContent>
    </HoverCard>

    <HoverCard v-if="recurringSummary">
      <HoverCardTrigger as-child>
        <button type="button" class="task-metadata-button text-emerald-600/60 hover:bg-emerald-50 hover:text-emerald-700" aria-label="Open recurring task details" @click.stop="emit('open')"><Repeat class="size-3.5" /></button>
      </HoverCardTrigger>
      <HoverCardContent class="w-64"><p class="text-sm font-medium">Recurring workflow</p><p class="mt-1 text-xs text-muted-foreground">{{ recurringSummary }}</p><p v-if="task.recurring?.endDate" class="mt-1 text-xs text-muted-foreground">Ends {{ task.recurring.endDate }}</p></HoverCardContent>
    </HoverCard>
  </div>
</template>

<style scoped>
.task-metadata-button {
  display: inline-flex;
  height: 1.5rem;
  width: 1.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  opacity: 0.7;
  transition: color 150ms, background-color 150ms, opacity 150ms;
}

.task-metadata-button:hover {
  opacity: 1;
}
</style>
